/**
 * This module is responsible for taking content from AEM and rewriting the scripts, links and images
 * to be served from the local server.  This is necessary because the AEM content comes in as relative
 * links to the AEM server.  We rewrite the links so that the content can be served from the local server.
 */
import * as cheerio from "cheerio";
import { AnyNode, Element } from "domhandler";

const rewriteUrl = (rewritePrefix: string, relativeUrl: string) => {
  if (!relativeUrl) return relativeUrl;

  // don't rewrite absolute URLs
  if (relativeUrl.toLowerCase().startsWith("http")) return relativeUrl;

  // this technically shouldn't exist, but it does in the AEM content
  // we handle that case by just prefixing with https
  if (relativeUrl.startsWith("//")) return "https:" + relativeUrl;

  // remove trailing and leading slashes
  if (rewritePrefix.endsWith("/"))
    rewritePrefix = rewritePrefix.substring(0, rewritePrefix.length - 1);
  if (relativeUrl.startsWith("/")) relativeUrl = relativeUrl.substring(1);

  return rewritePrefix + "/" + relativeUrl;
};

const rewriteScriptsSrc = (
  $: cheerio.CheerioAPI,
  element: cheerio.Cheerio<AnyNode>,
  rewritePrefix: string,
) => {
  const scripts: string[] = [];

  $(element)
    .find("script")
    .each((_, el) => {
      const src = $(el).attr("src");
      if (src) {
        scripts.push(rewriteUrl(rewritePrefix, src));
      } else {
        console.log(
          "found a <script> tag with no src ",
          $(el).html()?.substring(0, 500),
        );
      }
    });

  return scripts;
};

/**
 * return an array of script src attributes for all scripts under tag
 *
 * @param tag - the html tag name to search for scripts, e.g. head, body
 * @param aemUrl - the Adobe url to get the header content
 * @param localUrl - the local url to rewrite the script src attributes
 * @returns a string array of script src attributes for all scripts under tag
 */
const getScripts = async (
  tag: string,
  rewritePrefix: string,
  html: string,
): Promise<string[]> => {
  try {
    // parse the html doc and extract the scripts
    const $ = cheerio.load(html);

    const scripts: string[] = [];
    const nodes = $(tag);

    // console.log("original head:", $(head).html());
    scripts.push(...rewriteScriptsSrc($, nodes, rewritePrefix));

    // console.log("scripts: ", scripts);
    return scripts;
  } catch (err) {
    console.error("Error getting scripts");
    console.dir(err);
    return [];
  }
};

const rewriteAttribute = (
  el: cheerio.Cheerio<Element>,
  attributeName: string,
  rewritePrefix: string,
) => {
  const attribute = el.attr(attributeName);

  if (attribute) {
    const newAttribute = rewriteUrl(rewritePrefix, attribute);
    // console.log(`attribute ${attributeName} newAttribute: `, newAttribute);
    el.attr(attributeName, newAttribute);
  }
};

const rewriteImagePaths = ($: cheerio.CheerioAPI, rewritePrefix: string) => {
  // parse the html doc and fix up img tag urls

  // console.log("original content:", $.html());

  $("img").each((_, el) => {
    rewriteAttribute($(el), "src", rewritePrefix);

    // srcset is a comma separated list of urls. break that down and fix each url
    const srcSet = $(el).attr("srcset");
    if (srcSet) {
      const srcSetArray = srcSet.split(",");
      const newSrcSetArray = srcSetArray.map((s) => {
        const newSrc = rewriteUrl(rewritePrefix, s);
        return newSrc;
      });
      const newSrcSet = newSrcSetArray.join(",");
      // console.log("newSrcSet: ", newSrcSet);
      $(el).attr("srcset", newSrcSet);
    }
  });

  // fix adobe images as well. these are divs with image related attributes
  $('div[data-cmp-is="image"]').each((_, el) => {
    rewriteAttribute($(el), "data-cmp-src", rewritePrefix);
    // cspell:ignore filereference
    rewriteAttribute($(el), "data-cmp-filereference", rewritePrefix);
  });

  return $;
};

const removeScripts = ($: cheerio.CheerioAPI) => {
  $("script").each((_, el) => {
    // console.log('removing script: ', $(el).toString());
    $(el).remove();
  });

  return $;
};

export const getAemHeadScripts = async (
  rewritePrefix: string,
  html: string,
): Promise<string[]> => {
  try {
    const scripts = await getScripts("head", rewritePrefix, html);

    // console.log("scripts: ", scripts);
    return scripts;
  } catch (err) {
    console.error("Error getting scripts");
    console.dir(err);
    return [];
  }
};

export const getAemBodyScripts = async (
  rewritePrefix: string,
  html: string,
): Promise<string[]> => {
  try {
    const scripts = await getScripts("body", rewritePrefix, html);

    // console.log("scripts: ", scripts);
    return scripts;
  } catch (err) {
    console.error("Error getting scripts");
    console.dir(err);
    return [];
  }
};

export const getAemHeadLinks = async (
  rewritePrefix: string,
  html: string,
): Promise<string[]> => {
  try {
    // parse the html doc and extract the links
    const $ = cheerio.load(html);

    const links: string[] = [];
    const head = $("head");

    // console.log("original head:", $(head).html());

    $(head)
      .find("link")
      .each((_, el) => {
        // console.log("script: ", $(el));
        const href = $(el).attr("href");
        const rel = $(el).attr("rel");

        if (rel && rel !== "stylesheet") {
          console.log(
            'warning: found a <link> tag with rel other than "stylesheet"',
          );
        }

        if (href) {
          links.push(rewriteUrl(rewritePrefix, href));
        } else {
          console.log("found a <link> tag with no href ", $(el).html());
        }
      });

    return links;
  } catch (err) {
    console.error("Error getting scripts");
    console.dir(err);
    return [];
  }
};

export const getAemHeader = async (rewritePrefix: string, html: string) => {
  try {
    const $ = cheerio.load(html);
    removeScripts($);
    rewriteImagePaths($, rewritePrefix);

    // the header comes in as a full html document.  only return body content
    const content = $("body").html();

    return content;
  } catch (err) {
    console.error("Error getting header");
    console.dir(err);
    return "";
  }
};

export const getAemFooter = async (rewritePrefix: string, html: string) => {
  try {
    const $ = cheerio.load(html);
    rewriteImagePaths($, rewritePrefix);
    let content = $("body").html();

    // for some reason the fragment coming back from AEM doesn't include the <footer> or <div> below
    // so we manually wrap that around the fragment so that the styling of the footer is correct
    content =
      // cspell:ignore experiencefragment
      '<footer class="experiencefragment aem-GridColumn aem-GridColumn--default--12">' +
      '<div class="experiencefragment cmp-experiencefragment--footer">' +
      content +
      "</div>" +
      "</footer>";

    // console.log("fixed footer: ", content);
    return content;
  } catch (err) {
    console.error("Error getting footer");
    console.dir(err);
    return "";
  }
};
